import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Partner Network gift card rewards for a VALUTTEN subscriber's broker: the
 * referral fees a partner (e.g. a buyer's agent) owes the broker, redeemed as a
 * gift card. Loyalty-codebase callables, called by name. Money is integer cents
 * inc GST; times are ISO strings or null.
 */

export interface GiftCardCatalogueItem {
  key: string;
  label: string;
  kind: string | null;
  /** Days to spend the card after it is sent; null = the card's own default. */
  expiryDays?: number | null;
}

export interface PartnerRewards {
  partnerId: string;
  partnerName: string;
  rewards: { enabled: boolean; mode: 'demo' | 'live' | string | null; catalogue: GiftCardCatalogueItem[] } | null;
  available: { amountIncGstCents: number; entryCount: number } | null;
}

export interface TenantGiftCard {
  giftCardId: string;
  partnerId: string;
  /** Server field (giftCardView): "Broker, Company" for a tenant card. */
  memberName?: string | null;
  brokerName?: string | null;
  brokerDocId?: string | null;
  valueCents: number;
  catalogueLabel?: string | null;
  /** Days to spend it after it is sent, as disclosed at request; null when the card's default applies. */
  expiryDays?: number | null;
  deliveryMethod: string;
  status: 'requested' | 'issuing' | 'issued' | 'cancelled' | string;
  reference?: string | null;
  /** 'awaiting_float': the partner's gift card float ran out; the card issues once it is topped up. The fees stay reserved. */
  holdReason?: 'awaiting_float' | string | null;
  mode: 'demo' | 'live' | string;
  requestedAt: string | null;
  issuedAt?: string | null;
}

export interface TenantGiftCardsResponse {
  broker?: { brokerDocId: string; name: string };
  partners: PartnerRewards[];
  giftCards: TenantGiftCard[];
}

/** Server messages for these codes are written for end users; anything else gets our wording. */
export function giftCardErrorMessage(err: unknown): string {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  const msg = typeof e.message === 'string' ? e.message.trim() : '';
  if (['failed-precondition', 'invalid-argument', 'resource-exhausted', 'not-found', 'permission-denied'].includes(code) && msg) return msg;
  if (code === 'unauthenticated') return 'Your session has expired. Sign in again.';
  if (code === 'unavailable' || code === 'deadline-exceeded') return 'We could not reach the server. Check your connection and try again.';
  return 'Something went wrong. Please try again.';
}

export interface UsePartnerGiftCardsResult {
  data: TenantGiftCardsResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  request: (input: { partnerId: string; catalogueKey: string }) => Promise<TenantGiftCard>;
  cancel: (giftCardId: string) => Promise<void>;
}

/**
 * `brokerDocId` is sent when the broker is not the caller's own profile (an
 * owner, admin or assistant redeeming on a broker's behalf). `enabled` holds
 * the first load until the broker scope is known, so a caller without a
 * broker profile never sends a request the server must refuse.
 */
export function usePartnerGiftCards(brokerDocId: string | null, enabled = true): UsePartnerGiftCardsResult {
  const [data, setData] = useState<TenantGiftCardsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const fn = httpsCallable<{ brokerDocId?: string }, TenantGiftCardsResponse>(functions, 'tenantListGiftCards');
      const res = await fn(brokerDocId ? { brokerDocId } : {});
      setData({
        partners: Array.isArray(res.data?.partners) ? res.data.partners : [],
        giftCards: Array.isArray(res.data?.giftCards) ? res.data.giftCards : [],
      });
      setError(null);
    } catch (err) {
      setError(giftCardErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [brokerDocId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const request = useCallback(
    async (input: { partnerId: string; catalogueKey: string }) => {
      const fn = httpsCallable<
        { partnerId: string; catalogueKey: string; deliveryMethod: 'Email'; brokerDocId?: string },
        TenantGiftCard
      >(functions, 'tenantRequestGiftCard');
      const res = await fn({ ...input, deliveryMethod: 'Email', ...(brokerDocId ? { brokerDocId } : {}) });
      return res.data;
    },
    [brokerDocId]
  );

  const cancel = useCallback(async (giftCardId: string) => {
    const fn = httpsCallable<{ giftCardId: string }, { ok: boolean }>(functions, 'tenantCancelGiftCardRequest');
    await fn({ giftCardId });
  }, []);

  return { data, loading, error, reload, request, cancel };
}
